/**
 * IndustryDetailPanel — bubble-detail panel shown BELOW
 * IndustryPipelineOverview when an industry bubble is clicked.
 *
 * Uses the industry-level GetUcPipelineDetail (input.ucName omitted) — a
 * single call returns every UC's opps under the industry, grouped by stage.
 * Each opp carries its own ucName, which surfaces on the company row inside
 * <StageBreakdown/>.
 *
 * KPI grid uses the IndustryBubble props for the top row (Snapshot / Opps /
 * ARR / Status), then derives the four influence KPIs from the industry-level
 * detail response (rolled up from every stage's stageInsight).
 */

import type { IndustryBubble } from './PipelineMap';
import {
  KPI_INFO_TIPS,
  PipelineDetailShell,
  StatCell,
  deriveKpisFromStages,
  deriveTopKpisFromDetail,
  launchRatioLabel,
} from './PipelineDetailShell';

export default function IndustryDetailPanel({
  bubble,
  pipelineTypeFilter,
  pipelineFilter,
  snapshotDate,
  onClose,
}: {
  bubble: IndustryBubble;
  /** Selected value from the Pipeline类型 dropdown (Smithy PipelineType enum
   *  value, e.g. 'launched_pipelines'); undefined = All. Passed to the API
   *  (backend echoes it) AND applied client-side to filter opps until the
   *  server enforces the filter. */
  pipelineTypeFilter?: string;
  /** Property-tree JSON forwarded so the drill-in matches the bubble filter. */
  pipelineFilter?: string;
  /** Same snapshotDate driving the chart above; keeps the drill-in view
   *  time-consistent with the bubble the user clicked. */
  snapshotDate?: string;
  onClose: () => void;
}) {
  return (
    <PipelineDetailShell
      title={bubble.industry}
      fetch={{ industry: bubble.industry }}
      pipelineTypeFilter={pipelineTypeFilter}
      pipelineFilter={pipelineFilter}
      snapshotDate={snapshotDate}
      showUcNameInStages
      loadingLabel={`加载 ${bubble.industry} 行业明细…`}
      errorHeader="无法加载行业明细"
      onClose={onClose}
      renderKpis={(detail) => {
        const kpis = deriveKpisFromStages(detail?.stages);
        // Top-half KPIs come from the SAME detail response as the stage list
        // below, so a filter change re-fetches and both roll-ups update in
        // lockstep. Reading from the bubble snapshot would show stale values
        // (the bubble is captured at click time and doesn't follow the chart's
        // subsequent filter changes).
        const top = deriveTopKpisFromDetail(detail);
        const arrM = top ? (top.totalArr / 1e6).toFixed(2) : '—';
        // Pipeline 平均参与深度 sources from bubble.influenceDensityOpp
        // (industry roll-up = weighted avg over UCs) so it matches the
        // bubble chart X-axis exactly.
        const depth = bubble.influenceDensityOpp.toFixed(2);
        return (
          <>
            <StatCell label="Industry" value={bubble.industry} />
            <StatCell label="Opps" value={top?.oppCount ?? '—'} />
            <StatCell label="ARR" value={top ? `$${arrM}M` : '—'} />
            <StatCell
              label="Launch Ratio"
              value={top ? launchRatioLabel(top.launchedArr, top.totalArr) : '—'}
            />
            <StatCell
              label="Pipeline平均参与深度"
              value={depth}
              info={KPI_INFO_TIPS.pipelineDepth}
            />
            <StatCell
              label="渗透率"
              value={kpis.penetrationPct}
              info={KPI_INFO_TIPS.penetration}
            />
            <StatCell
              label="强相关opps数量"
              value={kpis.stronglyTouchedOpps}
              info={KPI_INFO_TIPS.stronglyTouchedOpps}
            />
            <StatCell
              label="IVT相关opps数量"
              value={kpis.touchedOpps}
              info={KPI_INFO_TIPS.touchedOpps}
            />
          </>
        );
      }}
    />
  );
}
